/**
 * 存在知识详情时的变更
 */
import { useTranslate } from '@tolgee/react';
import { Button, Divider, message as antdMessage } from 'antd';
import { useAdjustRelateKnowledge } from './useAdjustRelateKnowledge';
import { BuyerQuestion } from '../Detail/BuyerQuestion';
import { AIReply } from '../Detail/AIReply';
import {
  SaveKnowledge,
  saveSupplementalKnowledge,
  SupplementKnowledgeAnswer,
  SupplementKnowledgeDetailResponse,
  testSupplementalKnowledge,
  TestSupplementalKnowledgeData,
  updateSupplementKnowledgeV2,
} from 'services/api/supplementKnowledge';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CiteKnowledge from '../CiteKnowledge';
import { concat, last } from 'lodash-es';
import { SubmitAnswer, Action } from './SubmitAnswer';
import { useMemoizedFn } from 'ahooks';
import { messageToAnswer } from 'components/Dialogue/components/QAPair/utils';
import { ProductInfo } from './ProductInfo';
import { GenerateLoading } from '../Detail/GenerateLoading';
import { eventBus } from 'services/eventBus';

interface Props {
  message?: Conversation.Message;
  conversation?: Conversation.Conversation;
  supplementKnowledgeDetail: SupplementKnowledgeDetailResponse;
  onDone?: () => void;
  onDelayRefresh?: () => void;
  onRefresh?: () => void;
}

export const ContentWithDetail = (props: Props) => {
  const { message, conversation, supplementKnowledgeDetail, onDone, onDelayRefresh, onRefresh } =
    props;
  const {
    id: knowledgeId,
    itemIdentifier,
    supplementalKnowledgeValue,
    expandCount,
  } = supplementKnowledgeDetail;

  const { t } = useTranslate();
  const { region, channel, storeId } =
    conversation ||
    (() => {
      const { region, externalType, storeId } = supplementKnowledgeDetail.storeInfo || {};
      return {
        region: region as General.UpperCaseRegion,
        channel: externalType as General.PlatformUpperCase,
        storeId: `${storeId}`,
      };
    })();
  const { hide: hideAdjustRelateKnowledge } = useAdjustRelateKnowledge();

  const [action, setAction] = useState<Action>('NONE');
  const [inputAnswers, setInputAnswers] = useState<SupplementKnowledgeAnswer[]>([]);
  const [knowledgeUpdate, setKnowledgeUpdate] = useState<SaveKnowledge[]>([]);
  const [selectedMessages, setSelectedMessages] = useState<Conversation.Message[]>([]);

  const [defaultApplyType, setDefaultApplyType] = useState<'link' | 'sku' | undefined>(undefined);
  const [skuSelected, setSkuSelected] = useState<string[]>([]);
  const [testData, setTestData] = useState<TestSupplementalKnowledgeData | undefined>();

  const aiReplyRef = useRef<HTMLDivElement>(null);
  const [aiReplyFlashKey, setAiReplyFlashKey] = useState(0);

  const { mutateAsync: testSubmit, isLoading: isTestLoading } = useMutation({
    mutationFn: testSupplementalKnowledge,
    onError: () => {
      antdMessage.error(t('translation.操作失败，请稍后再试'));
    },
  });

  const aiReply = useMemo(() => {
    return testData || supplementKnowledgeDetail.aiReply;
  }, [testData, supplementKnowledgeDetail.aiReply]);

  const hasInputValue = useCallback(() => {
    if (inputAnswers.length > 0) {
      const textAnswers = inputAnswers.filter((answer) => answer.msgType === 'TEXT');
      const mediaAnswers = inputAnswers.filter((answer) => answer.msgType !== 'TEXT');
      return (
        textAnswers.every((answer) => answer.message?.trim() !== '') || mediaAnswers.length > 0
      );
    }
    return false;
  }, [inputAnswers]);

  const scrollToTop = () => {
    if (aiReplyRef.current) {
      aiReplyRef.current.scrollIntoView({
        block: 'center',
        inline: 'center',
        behavior: 'smooth',
      });
      setAiReplyFlashKey((key) => key + 1);
    }
  };

  const canSubmit = useMemo(() => {
    if (knowledgeUpdate.length > 0) {
      return true;
    }
    if (action === 'INPUT') {
      return hasInputValue();
    }
    if (action === 'SELECT' && selectedMessages.length > 0) {
      return true;
    }
    return false;
  }, [knowledgeUpdate.length, action, selectedMessages.length, hasInputValue]);

  const getTestKnowledgeList = (): SaveKnowledge | undefined => {
    const { pointId } = supplementKnowledgeDetail;
    const { question, originQuestions } = supplementalKnowledgeValue;
    const lastQuestion = last(originQuestions);
    const questionMessageText = question ? question.message : lastQuestion?.message;
    if (!questionMessageText) {
      return;
    }
    const { spuId, skus, platform, region, shopId } = itemIdentifier;
    if (action === 'SELECT' && selectedMessages.length > 0) {
      return {
        knowledgeId,
        key: questionMessageText,
        value: messageToAnswer(selectedMessages),
        source: '客服提交答案',
        bindItem: [
          {
            spuId,
            platform: platform.toUpperCase() as General.PlatformUpperCase,
            region: region.toUpperCase() as General.UpperCaseRegion,
            shopId: shopId,
            skus: skus,
          },
        ],
        operate: 'UPDATE',
        pointId,
      };
    }
    if (action === 'INPUT' && inputAnswers.length > 0) {
      return {
        knowledgeId,
        key: questionMessageText,
        value: inputAnswers,
        source: '客服提交答案',
        bindItem: [
          {
            spuId,
            platform: platform.toUpperCase() as General.PlatformUpperCase,
            region: region.toUpperCase() as General.UpperCaseRegion,
            shopId: shopId,
            skus: skus,
          },
        ],
        operate: 'UPDATE',
        pointId,
      };
    }
  };

  const handleTest = async () => {
    const { originQuestions, question } = supplementalKnowledgeValue;
    const lastQuestion = last(originQuestions);
    const questionMsgId = question ? question.messageId : lastQuestion?.messageId;
    if (!questionMsgId) {
      return;
    }
    const testKnowledge = getTestKnowledgeList();
    const knowledgeList = testKnowledge
      ? concat(testKnowledge, knowledgeUpdate || [])
      : knowledgeUpdate;
    const response = await testSubmit({
      knowledgeId,
      storeId,
      msgId: questionMsgId,
      knowledgeList,
    });
    setTestData(response.data.data);
    eventBus.emit('CITE_KNOWLEDGE_SCROLL');
    scrollToTop();
  };

  /** 当存在已提交问题，则更新 */
  const saveKnowledgeWhenCaseExists = useMemoizedFn(async () => {
    const testReqId = testData?.testReqId;
    const isCiteKnowledgeChanged = knowledgeUpdate.length > 0;
    if (isCiteKnowledgeChanged || testReqId) {
      const { data } = await saveSupplementalKnowledge({
        knowledgeList: knowledgeUpdate || [],
        knowledgeId,
        testReqId,
      });
      if (!data.data?.needUpdate) {
        return;
      }
      if (action === 'NONE') {
        await updateSupplementKnowledgeV2({
          id: knowledgeId,
          manualReviewStatus: 'REVIEW_FAILED',
          modify: isCiteKnowledgeChanged,
        });
      }
    }
    if (action === 'INPUT') {
      if (hasInputValue()) {
        await updateSupplementKnowledgeV2({
          id: knowledgeId,
          manualReviewStatus: 'REVIEW_PASSED',
          supplementalKnowledgeValue: {
            rewriteAnswers: inputAnswers,
          },
        });
      } else {
        await updateSupplementKnowledgeV2({
          id: knowledgeId,
          manualReviewStatus: 'REVIEW_FAILED',
          modify: isCiteKnowledgeChanged,
        });
      }
    }
    if (action === 'SELECT') {
      if (selectedMessages.length > 0) {
        await updateSupplementKnowledgeV2({
          id: knowledgeId,
          manualReviewStatus: 'REVIEW_PASSED',
          supplementalKnowledgeValue: {
            rewriteAnswers: messageToAnswer(selectedMessages),
          },
        });
      } else {
        await updateSupplementKnowledgeV2({
          id: knowledgeId,
          manualReviewStatus: 'REVIEW_FAILED',
          modify: isCiteKnowledgeChanged,
        });
      }
    }
  });

  const { isLoading: isSaveLoading, mutateAsync: saveContent } = useMutation({
    mutationFn: saveKnowledgeWhenCaseExists,
    onSuccess: () => {
      antdMessage.success(t('已提交'));
      onDone?.();
    },
    onError: () => {
      antdMessage.error(t('translation.操作失败，请稍后再试'));
    },
  });

  useEffect(() => {
    if (itemIdentifier && supplementalKnowledgeValue) {
      const { skus } = itemIdentifier;
      const { skuIds } = supplementalKnowledgeValue;
      setSkuSelected(skuIds);
      setDefaultApplyType(skuIds.length === 0 || skuIds.length === skus?.length ? 'link' : 'sku');
    }
  }, [itemIdentifier, supplementalKnowledgeValue]);

  return (
    <div className="relative w-full h-full flex flex-col">
      {isTestLoading && <GenerateLoading />}
      <div className="bg-[var(--fett-erp-card-bg)] flex-1 pt-4 px-4 pb-[72px] overflow-y-auto">
        <div className="bg-[var(--fett-bg-elevated)]">
          {itemIdentifier && (
            <ProductInfo
              defaultApplyType={defaultApplyType}
              key={knowledgeId}
              knowledgeId={knowledgeId}
              spuId={itemIdentifier.spuId}
              expandCount={expandCount}
              title={itemIdentifier.title}
              price={itemIdentifier.price}
              unit={itemIdentifier.unit}
              imageUrl={itemIdentifier.imageUrl}
              skuInfo={itemIdentifier.skus}
              skuSelected={skuSelected}
              onSKUChange={setSkuSelected}
              onDelayRefresh={onDelayRefresh}
              onRefresh={onRefresh}
            />
          )}
          <div className="mt-4" />
          <BuyerQuestion
            autoLocate={false}
            conversation={conversation}
            supplementalKnowledgeValue={supplementalKnowledgeValue}
            knowledgeId={knowledgeId}
            showKnowledgeSelector={false}
          />
          <div className="mt-4" />
          <div ref={aiReplyRef}>
            <AIReply aiReply={aiReply} flashKey={aiReplyFlashKey} />
          </div>
          <Divider className="!my-4" />
          <CiteKnowledge
            caseId={knowledgeId}
            applySkuIds={skuSelected}
            conversation={{ region, channel, storeId }}
            aiReply={aiReply}
            onKnowledgesUpdate={(knowledges) => {
              setKnowledgeUpdate(knowledges.flat());
            }}
          />
          <Divider className="!my-4" />
          <SubmitAnswer
            action={action}
            conversation={conversation}
            replyMessage={message}
            setAction={setAction}
            region={region}
            channel={channel}
            storeId={storeId}
            answers={inputAnswers}
            onAnswersChange={setInputAnswers}
            selectedMessages={selectedMessages}
            onSelectedMessagesChange={setSelectedMessages}
          />
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 w-full h-[56px] px-4 bg-[var(--fett-bg-elevated)] border-t border-solid border-[var(--fett-border-default)] shadow flex items-center justify-end gap-4">
        <Button onClick={hideAdjustRelateKnowledge}>{t('退出')}</Button>
        <Button ghost loading={isTestLoading} type="primary" onClick={handleTest}>
          {t('测试')}
        </Button>
        {canSubmit && (
          <>
            <Button type="primary" loading={isSaveLoading} onClick={() => saveContent()}>
              {t('保存修改内容')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
